/**
 * McpInstallCard — the "Connect Zibby to your AI agent" card on
 * /integrations. Three install paths, ranked by friction (lowest first):
 *
 *   1. One-line CLI:  `npx -y @zibby/cli@latest mcp install --all`
 *      Mints a PAT, writes the config block to every supported agent's
 *      config file. User restarts the agent + has 15 zibby_* tools.
 *
 *   2. Per-agent CLI: `npx -y @zibby/cli@latest mcp install --agent <x>`
 *      Same thing, scoped to one agent. Useful when the user only runs
 *      one client and doesn't want extra PATs floating around.
 *
 *   3. Manual config: the literal JSON/TOML block they paste into the
 *      agent's config file. Needed when the CLI can't run (sandboxed
 *      machine, Windows without npm, etc.).
 *
 * The PAT shown in (3) is a placeholder — `YOUR_ZIBBY_PAT`. User has to
 * mint one at /settings/tokens. (Auto-injecting their session here would
 * require a fresh PAT mint on page load, which is wasteful + leaks
 * tokens into render trees / DOM.)
 */
import React, { useState } from 'react';
import { IonCard, IonIcon } from '@ionic/react';
import {
  checkmarkCircle, copyOutline, terminalOutline,
  logoNpm, sparklesOutline, openOutline,
} from 'ionicons/icons';
import './McpInstallCard.css';

const API_URL = 'https://api-prod.zibby.app/mcp';

const AGENTS = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    flag: 'claude-code',
    syntax: 'json',
    configPath: '~/.claude/settings.json',
    config: {
      mcpServers: {
        zibby: {
          url: API_URL,
          headers: { Authorization: 'Bearer YOUR_ZIBBY_PAT' },
        },
      },
    },
  },
  {
    id: 'claude-desktop',
    name: 'Claude Desktop',
    flag: 'claude-desktop',
    syntax: 'json',
    configPath: '~/Library/Application Support/Claude/claude_desktop_config.json',
    config: {
      mcpServers: {
        zibby: {
          url: API_URL,
          headers: { Authorization: 'Bearer YOUR_ZIBBY_PAT' },
        },
      },
    },
  },
  {
    id: 'codex',
    name: 'Codex CLI',
    flag: 'codex',
    syntax: 'toml',
    configPath: '~/.codex/config.toml',
    // Codex doesn't put secrets inline — bearer_token_env_var points
    // at an env var the user exports. Render the literal block.
    config: [
      '[mcp_servers.zibby]',
      `url = "${API_URL}"`,
      'bearer_token_env_var = "ZIBBY_PAT"',
    ].join('\n'),
    extraStep: 'Then export your PAT:\n\n  export ZIBBY_PAT=zby_pat_…',
  },
  {
    id: 'gemini',
    name: 'Gemini CLI',
    flag: 'gemini',
    syntax: 'json',
    configPath: '~/.gemini/settings.json',
    config: {
      mcpServers: {
        zibby: {
          httpUrl: API_URL,  // Gemini uses `httpUrl`, not `url`
          headers: { Authorization: 'Bearer YOUR_ZIBBY_PAT' },
        },
      },
    },
  },
];

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`mcp-copy-btn${copied ? ' is-copied' : ''}`}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch (e) {
          console.warn('Clipboard copy failed:', e);
        }
      }}
      aria-label={copied ? 'Copied' : label}
    >
      <IonIcon icon={copied ? checkmarkCircle : copyOutline} aria-hidden="true" />
      <span>{copied ? 'Copied' : label}</span>
    </button>
  );
}

const McpInstallCard = () => {
  const [active, setActive] = useState('claude-code');
  const [showManual, setShowManual] = useState(false);

  const activeAgent = AGENTS.find((a) => a.id === active) || AGENTS[0];
  const oneLiner = 'npx -y @zibby/cli@latest mcp install --all';
  const perAgentLiner = `npx -y @zibby/cli@latest mcp install --agent ${activeAgent.flag}`;
  // CI / no-session one-shot. Token comes from an env var so it never
  // hits shell history. User pastes the PAT once in their CI secrets;
  // every build can re-run this without an OAuth round.
  const ciLiner = `ZIBBY_PAT=zby_pat_… npx -y @zibby/cli@latest mcp install --all`;
  const configText = typeof activeAgent.config === 'string'
    ? activeAgent.config
    : JSON.stringify(activeAgent.config, null, 2);

  return (
    <IonCard className="mcp-install-card">
      <div className="mcp-install-header">
        <div className="mcp-install-icon">
          <IonIcon icon={sparklesOutline} aria-hidden="true" />
        </div>
        <div className="mcp-install-title-block">
          <h2>Connect Teamy to your AI agent</h2>
          <p className="mcp-install-subtitle">
            One MCP, every agent. Paste a single command and your agent gets
            access to all Teamy tools (deploy apps, trigger workflows,
            tail logs, set instance missions).
          </p>
        </div>
      </div>

      <div className="mcp-install-section mcp-install-primary">
        <div className="mcp-install-section-head">
          <span className="mcp-install-step">1</span>
          <span className="mcp-install-section-title">Run this command (configures all 5 agents):</span>
        </div>
        <div className="mcp-install-codeblock">
          <pre>
            <span className="mcp-install-prompt"><IonIcon icon={terminalOutline} aria-hidden="true" />$</span>
            <code>{oneLiner}</code>
          </pre>
          <CopyButton text={oneLiner} />
        </div>
        <p className="mcp-install-hint">
          Reads your existing <code>zibby login</code> session, mints a fresh PAT
          scoped per agent, and merges the right config block into each
          agent's settings file. Then restart the agent — done.
          {' '}If you don't want to run <code>zibby login</code> first (CI, scripts),
          pass the token inline — see step 3.
        </p>
      </div>

      <div className="mcp-install-section">
        <div className="mcp-install-section-head">
          <span className="mcp-install-step">2</span>
          <span className="mcp-install-section-title">Pick just one agent (optional):</span>
        </div>
        <div className="mcp-install-tabs" role="tablist">
          {AGENTS.map((a) => (
            <button
              key={a.id}
              type="button"
              role="tab"
              aria-selected={active === a.id}
              className={`mcp-install-tab${active === a.id ? ' is-active' : ''}`}
              onClick={() => setActive(a.id)}
            >
              {a.name}
            </button>
          ))}
        </div>
        <div className="mcp-install-codeblock">
          <pre>
            <span className="mcp-install-prompt"><IonIcon icon={terminalOutline} aria-hidden="true" />$</span>
            <code>{perAgentLiner}</code>
          </pre>
          <CopyButton text={perAgentLiner} />
        </div>
      </div>

      <div className="mcp-install-section">
        <div className="mcp-install-section-head">
          <span className="mcp-install-step">3</span>
          <span className="mcp-install-section-title">No login? One-shot with a PAT (CI / scripts):</span>
        </div>
        <div className="mcp-install-codeblock">
          <pre>
            <span className="mcp-install-prompt"><IonIcon icon={terminalOutline} aria-hidden="true" />$</span>
            <code>{ciLiner}</code>
          </pre>
          <CopyButton text={ciLiner} />
        </div>
        <p className="mcp-install-hint">
          Generate a PAT at{' '}
          <a href="/settings/tokens" className="mcp-install-link">Settings → Tokens</a>,
          export it as <code>ZIBBY_PAT</code>, and the install runs without an
          OAuth round-trip. Works in Docker, GitHub Actions, devcontainers, etc.
        </p>
      </div>

      <div className="mcp-install-section mcp-install-manual">
        <button
          type="button"
          className="mcp-install-manual-toggle"
          onClick={() => setShowManual((v) => !v)}
          aria-expanded={showManual}
        >
          {showManual ? '▼' : '▶'} Show manual config (no CLI)
        </button>
        {showManual && (
          <div className="mcp-install-manual-body">
            <p className="mcp-install-hint">
              {' '}Paste the block below into <code>{activeAgent.configPath}</code>. Replace{' '}
              <code>YOUR_ZIBBY_PAT</code> with a token from{' '}
              <a href="/settings/tokens" className="mcp-install-link">
                Settings → Tokens <IonIcon icon={openOutline} aria-hidden="true" />
              </a>.
            </p>
            <div className="mcp-install-codeblock mcp-install-codeblock--config">
              <pre><code>{configText}</code></pre>
              <CopyButton text={configText} />
            </div>
            {activeAgent.extraStep && (
              <pre className="mcp-install-extrastep">{activeAgent.extraStep}</pre>
            )}
          </div>
        )}
      </div>

      <div className="mcp-install-footer">
        <a
          className="mcp-install-link"
          href="https://www.npmjs.com/package/@zibby/cli"
          target="_blank"
          rel="noreferrer noopener"
        >
          <IonIcon icon={logoNpm} aria-hidden="true" /> @zibby/cli on npm
        </a>
        <a className="mcp-install-link" href="/settings/tokens">
          Manage Personal Access Tokens <IonIcon icon={openOutline} aria-hidden="true" />
        </a>
      </div>
    </IonCard>
  );
};

export default McpInstallCard;
